import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  ImageCroppedEvent,
  ImageCropperComponent,
  ImageTransform,
} from 'ngx-image-cropper';
import { Capacitor } from '@capacitor/core';
import { addIcons } from 'ionicons';
import { Subscription } from 'rxjs';
import {
  addCircle,
  refresh,
  codeOutline,
  chevronExpandOutline,
  closeCircleOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import {
  IonIcon,
  IonModal,
  IonRow,
  IonCol,
  IonButton,
  IonPopover,
  IonImg,
  IonSkeletonText,
} from '@ionic/angular/standalone';
import { CharityService } from '../../../core/services/charity/charity.service';
import { AlertService } from '../../../core/services/navigation/alert.service';
import { LoggerService } from '../../../core/services/logger/logger.service';
import { downscaleImageFile, toJpg } from '../../../core/utilities/image-resize.util';
import { Charity } from 'src/app/core/models/Charity';

@Component({
    selector: 'app-charity-avatar',
    templateUrl: './charity-avatar.component.html',
    styleUrls: ['./charity-avatar.component.scss'],
    imports: [ImageCropperComponent, IonIcon, IonModal, IonRow, IonCol, IonButton, IonPopover, IonImg, IonSkeletonText]
})
export class CharityAvatarComponent implements OnInit {
  @ViewChild('cropper') cropper!: ImageCropperComponent;
  @Input() set avatarUrl(url: string | null) {
    if (url) {
      this.downloadImage();
    }
  }
  @Output() upload = new EventEmitter<File>();

  _avatarUrl!: string;
  imageFile?: File;
  transform: ImageTransform = {};
  maintainAspectRatio: boolean = true;
  isMobile = Capacitor.getPlatform() !== 'web';
  isModalOpen = false;
  charityId!: string;
  charity!: Charity;
  isImageLoaded: boolean = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private charityService: CharityService,
    private readonly alertService: AlertService,
    private readonly logger: LoggerService,
  ) {
    addIcons({
      addCircle,
      refresh,
      codeOutline,
      chevronExpandOutline,
      closeCircleOutline,
      checkmarkCircleOutline,
    });
  }

  ngOnInit(): void {
    if (this.avatarUrl) {
      this._avatarUrl = this.avatarUrl;
    }
    this.getCharityInfo();
  }

  getCharityInfo() {
    this.subscriptions.add(
      this.charityService.charity$.subscribe(charity => {
        if (charity?.id) {
          this.charityId = charity.id;
          this.charity = charity
          if (!charity.avatarUrl) {
            this.charity.avatarUrl = 'assets/ic-charity.svg';
          }
        }
      }),
    );
  }

  downloadImage() {
    // Download image from server
  }

  async selectImage(e: Event): Promise<void> {
    const input: HTMLInputElement = e.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];
    if (!file) {
      return;
    }
    this.maintainAspectRatio = true;
    try {
      this.imageFile = await downscaleImageFile(file);
      this.setOpen(true);
    } catch (error: unknown) {
      this.logger.error('Failed to prepare selected charity image', error, { fileSize: file.size });
      this.alertService.showAlertMessage('Upload failed', 'We could not open that image. Please try a different photo.');
    } finally {
      input.value = '';
    }
  }

  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
  }

  imageCropped(event: ImageCroppedEvent) {
    this._avatarUrl = URL.createObjectURL(event.blob!);
    toJpg(this._avatarUrl, { name: 'avatar.jpg' }).then((file: File) => {
      URL.revokeObjectURL(this._avatarUrl);
      this.upload.emit(file);
    }).catch((error: unknown) => {
      this.logger.error('Failed to convert charity image', error);
      this.alertService.showAlertMessage('Upload failed', 'We could not process that image. Please try a different photo.');
    });
    this.setOpen(false);
  }

  imageLoaded(): void {
    setTimeout(() => {
      this.maintainAspectRatio = false;
    }, 500);
  }

  loadImageFailed() {
    this.setOpen(false);
    this.logger.error('Cropper failed to load charity image');
    this.alertService.showAlertMessage('Upload failed', 'We could not open that image. Please try a different photo.');
  }

  cropImage() {
    this.cropper.crop();
    this.setOpen(false);
  }

  discardChanges() {
    this.setOpen(false);
  }

  // Image edit functions
  rotate() {
    const newValue: number = ((this.transform.rotate ?? 0) + 90) % 360;

    this.transform = {
      ...this.transform,
      rotate: newValue,
    };
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

}
